import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { ExitIntent } from "@/components/marketing/Conversion";
import { api, track, waLink, formatINR } from "@/lib/api";
import { useSEO, breadcrumbSchema } from "@/lib/seo";

export default function Pricing() {
  const navigate = useNavigate();
  const [cms, setCms] = useState({ plans: [], faqs: [], settings: {} });
  const [annual, setAnnual] = useState(false);

  useSEO({
    title: "Pricing — Mashara Finance | Transparent Plans for Every Organization",
    description: "Starter ₹999/mo, Professional ₹2,499/mo, Business ₹4,999/mo. Monthly or annual billing. 10-day free trial with ₹1 verification. Cancel anytime.",
    path: "/pricing",
    jsonLd: breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Pricing", path: "/pricing" }]),
  });

  useEffect(() => {
    track("pricing_view");
    api.get("/cms/content").then((r) => setCms(r.data)).catch(() => {});
  }, []);

  const plans = cms.plans || [];
  const trialDays = cms.settings?.trial_days || 10;
  const pricingFaqs = (cms.faqs || []).filter((f) =>
    /cost|trial|credit card|cancel|upgrade|downgrade|support/i.test(f.q)
  ).slice(0, 6);

  const savings = (p) => {
    const monthlyTotal = p.monthly_price * 12;
    if (!p.annual_price || p.annual_price >= monthlyTotal) return null;
    return Math.round((1 - p.annual_price / monthlyTotal) * 100);
  };

  return (
    <div data-testid="pricing-page" className="py-16 sm:py-24">
      <ExitIntent />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-12">
          <p className="text-xs font-semibold uppercase tracking-widest text-emerald-700 mb-3">Pricing</p>
          <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight text-slate-900" data-testid="pricing-headline">
            Transparent Pricing. No Surprises.
          </h1>
          <p className="mt-4 text-base sm:text-lg text-slate-600">
            Start with a {trialDays}-day free trial — activated with a ₹1 verification payment. Upgrade, downgrade or cancel anytime.
          </p>
          <div className="mt-8 inline-flex items-center bg-slate-100 rounded-full p-1" data-testid="billing-toggle">
            <button data-testid="billing-monthly-button" onClick={() => setAnnual(false)}
              className={`px-5 py-2 rounded-full text-sm font-semibold transition-colors ${!annual ? "bg-slate-900 text-white" : "text-slate-600"}`}>
              Monthly
            </button>
            <button data-testid="billing-annual-button" onClick={() => setAnnual(true)}
              className={`px-5 py-2 rounded-full text-sm font-semibold transition-colors ${annual ? "bg-slate-900 text-white" : "text-slate-600"}`}>
              Annual
              {plans.some(savings) && <span className="ml-2 text-xs bg-emerald-100 text-emerald-800 rounded-full px-2 py-0.5">Save up to {Math.max(...plans.map((p) => savings(p) || 0))}%</span>}
            </button>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-6 items-stretch">
          {plans.map((p) => {
            const save = savings(p);
            return (
              <div key={p.id} data-testid={`plan-card-${p.id}`}
                className={`relative rounded-2xl border bg-white p-8 flex flex-col shadow-sm ${p.popular ? "border-emerald-600 ring-2 ring-emerald-600/20" : "border-slate-200"}`}>
                {p.popular && (
                  <span data-testid="popular-badge" className="absolute -top-3 left-1/2 -translate-x-1/2 bg-emerald-700 text-white text-xs font-bold px-4 py-1 rounded-full">
                    MOST POPULAR
                  </span>
                )}
                <h2 className="font-display font-bold text-xl text-slate-900">{p.name}</h2>
                <p className="text-sm text-slate-500 mt-1">{p.tagline}</p>
                <div className="mt-6">
                  <span className="font-display text-4xl font-bold text-slate-900" data-testid={`plan-price-${p.id}`}>
                    {formatINR(annual ? Math.round(p.annual_price / 12) : p.monthly_price)}
                  </span>
                  <span className="text-slate-500 text-sm">/month</span>
                  {annual && save && (
                    <p className="text-xs text-emerald-700 font-semibold mt-1" data-testid={`plan-save-${p.id}`}>
                      Billed {formatINR(p.annual_price)}/year — Save {save}%
                    </p>
                  )}
                  {annual && !save && <p className="text-xs text-slate-500 mt-1">Billed {formatINR(p.annual_price)}/year</p>}
                </div>
                <ul className="mt-6 space-y-3 flex-1">
                  {(p.features || []).map((f) => (
                    <li key={f} className="flex items-start gap-2.5 text-sm text-slate-700">
                      <Check className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" /> {f}
                    </li>
                  ))}
                </ul>
                <Button data-testid={`plan-cta-${p.id}`}
                  onClick={() => { track("hero_cta_click", { location: "pricing", plan: p.id }); navigate(`/signup?plan=${p.id}`); }}
                  className={`mt-8 w-full rounded-xl py-6 font-semibold ${p.popular ? "bg-emerald-700 hover:bg-emerald-800 text-white" : "bg-slate-900 hover:bg-slate-800 text-white"}`}>
                  Start Free Trial
                </Button>
                <p className="mt-3 text-xs text-slate-500 text-center">{p.users} · {p.centers} · {p.support}</p>
              </div>
            );
          })}
        </div>

        {/* Enterprise */}
        <div className="mt-10 rounded-2xl bg-slate-900 text-white p-8 sm:p-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6" data-testid="enterprise-card">
          <div>
            <h2 className="font-display font-bold text-2xl">Enterprise / Custom</h2>
            <p className="text-slate-300 mt-2 max-w-xl text-sm leading-relaxed">
              More than 10 centers, custom integrations, dedicated onboarding or special compliance needs?
              We'll design a plan around your organization.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <Button data-testid="enterprise-sales-button" onClick={() => navigate("/demo")}
              className="rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white px-6">
              Talk to Sales
            </Button>
            <a href={waLink("Hello, I want help choosing the right Mashara Finance plan.")} target="_blank" rel="noopener noreferrer"
              data-testid="enterprise-whatsapp-button" onClick={() => track("whatsapp_click", { location: "pricing_enterprise" })}
              className="flex items-center justify-center gap-2 rounded-xl border border-slate-600 px-6 py-2.5 text-sm font-semibold hover:bg-white/10 transition-colors">
              <MessageCircle className="w-4 h-4" /> WhatsApp Us
            </a>
          </div>
        </div>

        {pricingFaqs.length > 0 && (
          <div className="max-w-3xl mx-auto mt-20">
            <h2 className="font-display text-2xl sm:text-3xl font-bold text-slate-900 text-center mb-8">Pricing Questions</h2>
            <Accordion type="single" collapsible>
              {pricingFaqs.map((f, i) => (
                <AccordionItem key={i} value={`pfaq-${i}`}>
                  <AccordionTrigger data-testid={`pricing-faq-${i}`} className="text-left font-medium text-slate-900">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-slate-600">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        )}
      </div>
    </div>
  );
}
